// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `layers` route body (PR-B, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `layers` route projector: the document-level LAYER table, PAGINATED by the offsetKey cursor,
// SORTED by a `key:dir` grammar, FIELD-projected by `?fields`. A DOCUMENT-LEVEL inventory — NOT
// space-scoped (the seam forces `resolvedSpaceId: null` + bypasses the unknown-scope neutralizer,
// `isDocumentLevelRoute`); a `?scope` does NOT filter it. A BODY only — the envelope / cursor shape /
// value validation live in the seam; registered via `registerRouteProjector('layers', projectLayers)`.
//
// `entityCount` is DERIVED per layer by counting `snapshot.entities` whose `layer === record.name`
// across the WHOLE document — so the route's binding tuple is GEOMETRY-derived: a geometry mutation
// (add/erase) changes a layer's entityCount WITHOUT touching the layer TABLE. Hence
// `routeRevisions: { geometryRevision, tableRevision }` (fullstack:963 — "Cursor биндит
// geometryRevision+tableRevision"). `snapshotRevision` rides as the page ANCHOR only (INV-9).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers. depcruise cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type RouteProjector,
  type SceneSnapshotRef,
  type SnapshotLayerRef,
} from "../sceneSummaryProjector";
import {
  compareFacetsBy,
  paginate,
  parseKeyDirSort,
  projectFacetFields,
  resolveProjectionMode,
} from "./projectionHelpers";

/** The LEAN CORE field set projected when `?fields` is ABSENT (byte economy). Every member is allowlisted. */
const LEAN_CORE_FIELDS = [
  "name",
  "color",
  "on",
  "frozen",
  "locked",
  "linetype",
  "lineweight",
  "plottable",
  "entityCount",
] as const satisfies readonly (keyof SnapshotLayerRef | "entityCount")[];

/** The legal `key:dir` sort KEYS (`count` sorts the DERIVED entityCount). */
const LAYER_SORT_KEYS = ["name", "color", "lineweight", "count"] as const;
type LayerSortKey = (typeof LAYER_SORT_KEYS)[number];

/** A layer record + its DERIVED `entityCount` (the projected row — the schema record plus the count). */
type LayerRow = SnapshotLayerRef & { readonly entityCount: number };

/**
 * Build the per-layer `entityCount` map: count entities ON each layer across the WHOLE document
 * (NOT space-scoped — `layers` is a document-level inventory). O(entities) once. A layer with no
 * entities counts 0 (its row still appears — the layer table is the source, not the entity walk).
 */
function buildEntityCounts(snapshot: SceneSnapshotRef): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const e of snapshot.entities) {
    counts.set(e.layer, (counts.get(e.layer) ?? 0) + 1);
  }
  return counts;
}

/**
 * Extract a layer row's value for a sort key: `name`/`color`/`lineweight` are the RAW record fields
 * (name a string; color/lineweight numbers); `count` is the DERIVED `entityCount`. The seam already
 * validated the key is allowlisted, so an unexpected key is unreachable (returns undefined → ties).
 */
function layerSortValue(row: LayerRow, key: LayerSortKey): string | number | undefined {
  switch (key) {
    case "name":
      return row.name;
    case "color":
      return row.color;
    case "lineweight":
      return row.lineweight;
    case "count":
      return row.entityCount;
    default:
      return undefined;
  }
}

/**
 * The `layers` route body. enumerate-table → attach derived entityCount → sort → (fullField | field-
 * project) → paginate. Binds `routeRevisions` to `{ geometryRevision, tableRevision }` (entityCount
 * geometry-derived; the LayerRecord itself table-derived). `snapshotRevision` rides as the page
 * ANCHOR only. `resolvedSpaceId: null` (document-level — the seam already forced it). byteTruncated:
 * false (the companion serializer owns byte-budget truncation).
 */
export const projectLayers: RouteProjector = ({ query, cursor, snapshot }) => {
  // ENUMERATE the full layer table (document-level — `?scope` does NOT filter it). Attach the
  // DERIVED entityCount so the projected row carries it + the sort can order by it.
  const counts = buildEntityCounts(snapshot);
  const rows: LayerRow[] = snapshot.layers.map((layer) => ({
    ...layer,
    entityCount: counts.get(layer.name) ?? 0,
  }));

  // SORT (before pagination — cursor-determinism). A COPY; a stable comparator preserves table order
  // on a tie. No `?sort` → the natural table order.
  // TIE-BREAK `name` ASC (fullstack:963 — `?sort=count` → DESC entityCount → name ASC; applies to
  // EVERY sort, so a color/lineweight/count tie also resolves to the documented `name` order rather
  // than the DXF table order, keeping pagination deterministic across an equal-primary run).
  const parsedSort = parseKeyDirSort<LayerSortKey>(query.sort, LAYER_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? rows
      : [...rows].sort((a, b) =>
          compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, layerSortValue, "name"),
        );

  // PROJECTION-MODE + LIMIT. `?fullField` forces limit=1 + projects only that field; else lean/`?fields`.
  const { fields, limit } = resolveProjectionMode(query, LEAN_CORE_FIELDS);

  // PAGINATION — the SHARED paginate primitive.
  const { slice, nextOffsetKey } = paginate(ordered, cursor, limit);

  // FIELD PROJECTION (INV-1 — only allowlisted fields). An OPTIONAL field absent on the row is omitted.
  const items = slice.map((row) => projectFacetFields(row as unknown as Record<string, unknown>, fields));

  return {
    items,
    nextOffsetKey,
    // ROUTE-SPECIFIC binding ([L7] / fullstack:963): entityCount is geometry-derived (geometryRevision),
    // the LayerRecord itself is table-derived (tableRevision). `snapshotRevision` is EXCLUDED (it bumps
    // on every commit → false-stale); it rides as the page ANCHOR only.
    routeRevisions: {
      geometryRevision: snapshot.revisions.geometryRevision,
      tableRevision: snapshot.revisions.tableRevision,
    },
    resolvedSpaceId: null, // document-level — not space-scoped.
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    total: ordered.length,
  };
};
